import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { Logo } from "./Logo";
import type { User } from "../types";

type NavLeaf = { to: string; label: string; icon: string; end?: boolean };
type NavGroup = { id: string; label: string; icon: string; children: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return "children" in entry;
}

const nav: NavEntry[] = [
  { to: "/", label: "Přehled", icon: "🏠", end: true },
  { to: "/jidelnicky", label: "Jídelníčky", icon: "📅" },
  { to: "/lednice", label: "Lednice", icon: "🧊" },
  {
    id: "katalogy",
    label: "Katalogy",
    icon: "📚",
    children: [
      { to: "/recepty", label: "Recepty", icon: "🍳" },
      { to: "/suroviny", label: "Suroviny", icon: "🥕" },
      { to: "/obchody", label: "Obchody", icon: "🏪" },
    ],
  },
  {
    id: "nastaveni",
    label: "Nastavení",
    icon: "⚙️",
    children: [{ to: "/rodina", label: "Rodina", icon: "👨‍👩‍👧" }],
  },
];

export function AppLayout({ user }: { user: User }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = useMutation({
    mutationFn: () => api.post("/api/auth/logout"),
    onSettled: () => {
      // Plný reload zaručí, že padne veškerý React Query stav i běžící
      // background refetche → žádné 401 v konzoli při přechodu na /login.
      window.location.replace("/login");
    },
  });

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b bg-white sticky top-0 z-20 print:hidden">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            className="md:hidden btn-ghost p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            <span className="block w-5 h-0.5 bg-current mb-1" />
            <span className="block w-5 h-0.5 bg-current mb-1" />
            <span className="block w-5 h-0.5 bg-current" />
          </button>

          <NavLink to="/" className="flex items-center gap-2 text-brand-700 font-bold text-lg">
            <Logo className="w-7 h-7" />
            <span>Jedalniček</span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {nav.map((entry) =>
              isGroup(entry) ? (
                <DesktopGroup key={entry.id} group={entry} />
              ) : (
                <DesktopLink key={entry.to} item={entry} />
              )
            )}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {user.pictureUrl && (
                <img src={user.pictureUrl} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
              )}
              <span className="text-slate-700">{user.name}</span>
            </div>
            <button
              className="btn-secondary text-xs flex items-center gap-1"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              title="Odhlásit se"
            >
              <span aria-hidden>🚪</span>
              <span>{logout.isPending ? "Odhlašuji…" : "Odhlásit"}</span>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <nav className="md:hidden border-t bg-white px-2 py-2 flex flex-col gap-1">
            {nav.map((entry) =>
              isGroup(entry) ? (
                <MobileGroup key={entry.id} group={entry} onNavigate={() => setMobileOpen(false)} />
              ) : (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn("nav-link flex items-center gap-2", isActive && "nav-link-active")
                  }
                >
                  <span aria-hidden className="text-lg w-5 text-center">{entry.icon}</span>
                  <span>{entry.label}</span>
                </NavLink>
              )
            )}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 py-6 print:max-w-none print:px-0 print:py-0">
          <Outlet />
        </div>
      </main>

      <footer className="border-t bg-white py-4 text-xs text-center text-slate-500 flex items-center justify-center gap-2 print:hidden">
        <Logo className="w-4 h-4 text-brand-600" />
        <span>Jedalniček · Cloudflare Pages</span>
      </footer>
    </div>
  );
}

function DesktopLink({ item }: { item: NavLeaf }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn("nav-link flex items-center gap-1.5", isActive && "nav-link-active")
      }
    >
      <span aria-hidden>{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

function DesktopGroup({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Skupina je vizuálně "aktivní", pokud aktuální cesta odpovídá jakékoli z jejích dětí.
  const isActive = group.children.some((c) => location.pathname.startsWith(c.to));

  // Při změně cesty zavřeme dropdown (pro případ, že navigace proběhla z jiného místa).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Klik mimo komponentu zavře dropdown.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cn("nav-link flex items-center gap-1.5", isActive && "nav-link-active")}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span aria-hidden>{group.icon}</span>
        <span>{group.label}</span>
        <span aria-hidden className={cn("text-xs transition-transform", open && "rotate-180")}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-1 min-w-[200px] rounded-md border border-slate-200 bg-white shadow-lg py-1"
        >
          {group.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50",
                  isActive && "bg-brand-50 text-brand-700 font-medium"
                )
              }
            >
              <span aria-hidden className="w-5 text-center">{c.icon}</span>
              <span>{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileGroup({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const location = useLocation();
  const activeInside = group.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(activeInside);

  return (
    <div>
      <button
        type="button"
        className={cn(
          "nav-link w-full flex items-center gap-2",
          activeInside && "nav-link-active"
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span aria-hidden className="text-lg w-5 text-center">{group.icon}</span>
        <span className="flex-1 text-left">{group.label}</span>
        <span aria-hidden className={cn("text-xs transition-transform", open && "rotate-180")}>▾</span>
      </button>
      {open && (
        <div className="pl-6 mt-1 mb-1 flex flex-col gap-0.5 border-l border-slate-200 ml-4">
          {group.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn("nav-link flex items-center gap-2 text-sm", isActive && "nav-link-active")
              }
            >
              <span aria-hidden className="w-5 text-center">{c.icon}</span>
              <span>{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
